import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Lightbox from 'yet-another-react-lightbox';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import Thumbnails from 'yet-another-react-lightbox/plugins/thumbnails';
import Counter from 'yet-another-react-lightbox/plugins/counter';
import Captions from 'yet-another-react-lightbox/plugins/captions';
import Download from 'yet-another-react-lightbox/plugins/download';
import 'yet-another-react-lightbox/styles.css';
import 'yet-another-react-lightbox/plugins/thumbnails.css';
import 'yet-another-react-lightbox/plugins/counter.css';
import 'yet-another-react-lightbox/plugins/captions.css';
import { apiUrl, pageImagePath, sourceImageUrl, sourceThumbUrl } from '../api/config';
import { useChatUi } from '../ChatContext';

const FULL_WIDTH = 2000; // nominal widths for srcSet selection; the real size is read after load
const THUMB_WIDTH = 360;

function pageSlide({ manualName, manualLabel, pageNumber, title, description, src, thumb, sourceId }) {
  const label = `${manualLabel || manualName || ''} p.${pageNumber + 1}`.trim();
  const slide = {
    src,
    alt: label,
    title: title || label,
    description: description || '',
    manualName,
    manualLabel,
    pageNumber,
    sourceId: sourceId || null,
    download: { url: src, filename: `${manualName || 'page'}_p${pageNumber + 1}.jpg` },
  };
  if (thumb && thumb !== src && !src.startsWith('data:')) {
    slide.srcSet = [
      { src: thumb, width: THUMB_WIDTH, height: Math.round(THUMB_WIDTH * 1.414) },
      { src, width: FULL_WIDTH, height: Math.round(FULL_WIDTH * 1.414) },
    ];
  }
  return slide;
}

/** Slides for one message: figure pages first, then the other sources, in id order. */
export function slidesForSources(sources) {
  const ordered = [...(sources || [])].sort((a, b) => (Number(!!b.figure) - Number(!!a.figure)) || (a.id - b.id));
  return ordered.map((s) => pageSlide({
    manualName: s.manual_name,
    manualLabel: s.manual_label,
    pageNumber: Number(s.page_number) || 0,
    title: `[${s.id}] ${[s.manual_label, s.page_label, s.printed_page].filter(Boolean).join(' · ')}`,
    description: [s.section, s.section_title || s.title].filter(Boolean).join(' '),
    src: sourceImageUrl(s),
    thumb: sourceThumbUrl(s),
    sourceId: s.id,
  }));
}

function neighborSlide(slide, delta) {
  const pageNumber = slide.pageNumber + delta;
  return pageSlide({
    manualName: slide.manualName,
    manualLabel: slide.manualLabel,
    pageNumber,
    src: apiUrl(pageImagePath(slide.manualName, pageNumber)),
    thumb: apiUrl(pageImagePath(slide.manualName, pageNumber, THUMB_WIDTH)),
  });
}

const Icon = ({ d }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {d.map((p) => <path key={p} d={p} />)}
  </svg>
);

/**
 * The single page viewer of the chat: a lightbox over one message's source pages with zoom (wheel, pinch,
 * double-click, +/-), drag-to-pan, keyboard navigation, thumbnails, captions, counter, download and
 * "open full size". The previous/next page buttons insert the neighbouring pages of the same manual so a
 * technician can browse around a cited page.
 *
 * @param {{request: ?{sources: Object[], sourceId: number, key: string}, onClose: function}} props
 */
export default function PageViewer({ request, onClose }) {
  const { t } = useChatUi();
  const [slides, setSlides] = useState([]);
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (!request) return;
    const next = slidesForSources(request.sources);
    const at = Math.max(0, next.findIndex((s) => s.sourceId === request.sourceId));
    setSlides(next);
    setIndex(at);
  }, [request]);

  const current = slides[index];
  const canBrowse = Boolean(current && current.manualName && !String(current.src).startsWith('data:'));

  const goToPage = useCallback((delta) => {
    if (!current || !canBrowse) return;
    const target = current.pageNumber + delta;
    if (target < 0) return;
    const existing = slides.findIndex((s) => s.manualName === current.manualName && s.pageNumber === target);
    if (existing !== -1) {
      setIndex(existing);
      return;
    }
    const slide = neighborSlide(current, delta);
    const insertAt = delta > 0 ? index + 1 : index;
    setSlides((prev) => [...prev.slice(0, insertAt), slide, ...prev.slice(insertAt)]);
    setIndex(insertAt);
  }, [current, canBrowse, slides, index]);

  const toolbarButtons = useMemo(() => [
    canBrowse && (
      <button key="prev-page" type="button" className="yarl__button cv-viewer-btn" title={t('Previous page')} aria-label={t('Previous page')} onClick={() => goToPage(-1)} disabled={!current || current.pageNumber <= 0}>
        <Icon d={['M15 18l-6-6 6-6']} />
        <span className="cv-viewer-btn__text">{t('Previous page')}</span>
      </button>
    ),
    canBrowse && (
      <button key="next-page" type="button" className="yarl__button cv-viewer-btn" title={t('Next page')} aria-label={t('Next page')} onClick={() => goToPage(1)}>
        <span className="cv-viewer-btn__text">{t('Next page')}</span>
        <Icon d={['M9 18l6-6-6-6']} />
      </button>
    ),
    'zoom',
    'download',
    <button key="open-tab" type="button" className="yarl__button" title={t('Open in new tab')} aria-label={t('Open in new tab')} onClick={() => current && window.open(current.src, '_blank', 'noopener')}>
      <Icon d={['M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6', 'M15 3h6v6', 'M10 14L21 3']} />
    </button>,
    'close',
  ].filter(Boolean), [canBrowse, current, goToPage, t]);

  return (
    <Lightbox
      open={Boolean(request) && slides.length > 0}
      close={onClose}
      index={index}
      slides={slides}
      on={{ view: ({ index: i }) => setIndex(i) }}
      plugins={[Zoom, Thumbnails, Counter, Captions, Download]}
      className="cv-viewer"
      carousel={{ finite: true, preload: 2, imageFit: 'contain', padding: '16px' }}
      controller={{ closeOnBackdropClick: true, closeOnPullDown: true }}
      zoom={{ maxZoomPixelRatio: 3, scrollToZoom: true, doubleClickMaxStops: 3, zoomInMultiplier: 1.6 }}
      thumbnails={{ width: 72, height: 96, border: 1, borderRadius: 4, padding: 2, gap: 8, imageFit: 'cover', vignette: false }}
      captions={{ descriptionTextAlign: 'start', descriptionMaxLines: 2, showToggle: true }}
      counter={{ container: { className: 'cv-viewer-counter', style: { top: 0, bottom: 'unset', left: '50%', right: 'unset', transform: 'translateX(-50%)' } } }}
      toolbar={{ buttons: toolbarButtons }}
      labels={{
        Previous: t('Previous'),
        Next: t('Next'),
        Close: t('Close'),
        'Zoom in': t('Zoom in'),
        'Zoom out': t('Zoom out'),
        Download: t('Download'),
      }}
    />
  );
}
